import clsx from 'clsx';
import {
  Children,
  type ElementType,
  forwardRef,
  Fragment,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react';

import * as styles from './BaseChip.sty.js';
import { ChipPrefix, ChipSuffix } from './ChipContent.js';
import { ChipDeleteButton } from './ChipDeleteButton.js';
import { ChipKey } from './ChipKey.js';
import { ChipContext } from './contexts/ChipContext.js';
import { useChipContext } from './contexts/useChipContext.js';
import type { ChipProps } from './types/chip.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { focusRingStatic } from '../../core/styles/focusRingStatic.sty.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { fieldTokens as fieldColorsCSS } from '../../styles/field.sty.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';
import { Text } from '../../typography/text/Text.js';

const COMPONENT_NAME = 'Chip';

//#region Typings
type ChipSlots = {
  deleteButton: ReactNode | null;
  key: ReactNode[];
  value: ReactNode[];
  prefixIcons: ReactNode[];
  suffixIcons: ReactNode[];
};
//#endregion Typings

/** Iterates over child nodes to extract specific component slots. */
const getChipSlots = (node: ReactNode) => {
  const slots: ChipSlots = {
    deleteButton: null,
    prefixIcons: [],
    suffixIcons: [],
    value: [],
    key: [],
  };

  for (const child of Children.toArray(node)) {
    if (isSlot(child, ChipPrefix)) {
      slots.prefixIcons.push(child);
    } else if (isSlot(child, ChipSuffix)) {
      slots.suffixIcons.push(child);
    } else if (isSlot(child, ChipKey)) {
      slots.key.push(child);
    } else if (isSlot(child, ChipDeleteButton)) {
      slots.deleteButton = child;
    } else {
      slots.value.push(child);
    }
  }

  return slots;
};

/** Chip component */
const RootBaseChip: <E extends ElementType = 'div'>(
  props: ChipProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    props: ChipProps<E>,
    forwardedRef: typeof props.ref,
  ) => {
    const {
      children,
      as = 'div' as E,
      variant = 'emphasized',
      color = 'neutral',
      size = 'default',
      disabled = false,
      maxWidth,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      ...remainingProps
    } = props;

    const ariaLabelingProps = useAriaLabelingProps(remainingProps);
    const forwardProps = useSafeForwardProps(remainingProps, ariaLabelingProps);

    const [behavioralTrackingProps] =
      useBehavioralTrackingProps(remainingProps);

    const { prefixIcons, suffixIcons, deleteButton, key, value } = useMemo(
      () => getChipSlots(children),
      [children],
    );

    const valueId = useId();
    const hasKey = key.length > 0;

    return (
      <Flex
        as="div"
        ref={forwardedRef}
        gap={2}
        className={clsx(
          styles.wrapper,
          disabled && styles.disabledWrapper,
          fieldColorsCSS({ variant, color, disabled }),
          consumerClassName,
        )}
        style={{
          maxWidth: maxWidth ?? 250,
          ...consumerStyle,
        }}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...behavioralTrackingProps}
        data-dt-component={dataDtComponent}
      >
        <ChipContext.Provider
          value={{ disabled, variant, color, size, valueId, hasKey }}
        >
          <Text
            as={as satisfies ElementType}
            textStyle="base"
            className={clsx(
              'strato-surface',
              focusRingStatic({ color, disabled, when: 'focus-visible' }),
              (as === 'a' || as === 'button') &&
                textStyleCSS({ textStyle: 'base-emphasized' }),
              fieldColorsCSS({ variant, color, disabled }),
              styles.container({
                size,
                disabled,
                color,
                variant,
                hasDeleteButton: deleteButton !== null,
              }),
            )}
            size={size}
            disabled={disabled}
            variant={variant}
            color={color}
            {...ariaLabelingProps}
            {...forwardProps}
          >
            {prefixIcons.map((node: ReactNode, index: number) => (
              <Fragment key={`prefix-node-${index}`}>{node}</Fragment>
            ))}
            <TextContainer keys={key} values={value} />
            {suffixIcons.map((node, index) => (
              <Fragment key={`suffix-node-${index}`}>{node}</Fragment>
            ))}
          </Text>
          {deleteButton}
        </ChipContext.Provider>
      </Flex>
    );
  },
);

function TextContainer({
  keys,
  values,
}: Readonly<{
  keys: ReactNode[];
  values: ReactNode[];
}>) {
  const { valueId: id } = useChipContext();
  const hasValue = values.length > 0;
  const hasKey = keys.length > 0;

  if (!(hasKey || hasValue)) {
    return null;
  }

  const separator = hasKey && hasValue ? <span>:</span> : null;

  return (
    <div className={styles.textContainer}>
      {keys.map((node: ReactNode, index: number) => (
        <Fragment key={`key-node-${index}`}>{node}</Fragment>
      ))}
      {separator}
      {values.map((node, index) => (
        <Flex key={`value-node-${index}`} id={id} className={styles.value}>
          {node}
        </Flex>
      ))}
    </div>
  );
}

/**
 * Use the `Chip` component to present a piece of information in a compact form.
 * @internal
 */
export const BaseChip = Object.assign(RootBaseChip, {
  Prefix: ChipPrefix,
  Suffix: ChipSuffix,
  Key: ChipKey,
  DeleteButton: ChipDeleteButton,
});

(BaseChip as typeof BaseChip & { displayName: string }).displayName =
  COMPONENT_NAME;
